import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, LogOut, Plane, Download, RefreshCw, Calendar, User, MapPin, Mail, X, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import HreflangTags from '../components/HreflangTags';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import REAL_FLIGHTS from '../data/realFlights';
import ToolSidebar from '../components/ToolSidebar';
import '../styles/KeyFlightPage.css';

const API = process.env.REACT_APP_BACKEND_URL;

const AIRPORTS = [
  { code: 'DXB', city: 'Dubai', country: 'UAE', name: 'Dubai International Airport' },
  { code: 'TAS', city: 'Tashkent', country: 'Uzbekistan', name: 'Islam Karimov Tashkent International Airport' },
  { code: 'SKD', city: 'Samarkand', country: 'Uzbekistan', name: 'Samarkand International Airport' },
  { code: 'BHK', city: 'Bukhara', country: 'Uzbekistan', name: 'Bukhara International Airport' },
  { code: 'NVI', city: 'Navoi', country: 'Uzbekistan', name: 'Navoi International Airport' },
  { code: 'SHJ', city: 'Sharjah', country: 'UAE', name: 'Sharjah International Airport' },
  { code: 'AUH', city: 'Abu Dhabi', country: 'UAE', name: 'Zayed International Airport' },
  { code: 'IST', city: 'Istanbul', country: 'Turkey', name: 'Istanbul Airport' },
  { code: 'LHR', city: 'London', country: 'United Kingdom', name: 'London Heathrow Airport' },
  { code: 'JFK', city: 'New York', country: 'United States', name: 'John F. Kennedy International Airport' },
  { code: 'CDG', city: 'Paris', country: 'France', name: 'Charles de Gaulle Airport' },
  { code: 'FRA', city: 'Frankfurt', country: 'Germany', name: 'Frankfurt Airport' },
  { code: 'DEL', city: 'New Delhi', country: 'India', name: 'Indira Gandhi International Airport' },
  { code: 'BOM', city: 'Mumbai', country: 'India', name: 'Chhatrapati Shivaji International Airport' },
  { code: 'KHI', city: 'Karachi', country: 'Pakistan', name: 'Jinnah International Airport' },
  { code: 'ISB', city: 'Islamabad', country: 'Pakistan', name: 'Islamabad International Airport' },
  { code: 'LHE', city: 'Lahore', country: 'Pakistan', name: 'Allama Iqbal International Airport' },
  { code: 'DAC', city: 'Dhaka', country: 'Bangladesh', name: 'Hazrat Shahjalal International Airport' },
  { code: 'CMB', city: 'Colombo', country: 'Sri Lanka', name: 'Bandaranaike International Airport' },
  { code: 'KTM', city: 'Kathmandu', country: 'Nepal', name: 'Tribhuvan International Airport' },
  { code: 'MNL', city: 'Manila', country: 'Philippines', name: 'Ninoy Aquino International Airport' },
  { code: 'BKK', city: 'Bangkok', country: 'Thailand', name: 'Suvarnabhumi Airport' },
  { code: 'KUL', city: 'Kuala Lumpur', country: 'Malaysia', name: 'Kuala Lumpur International Airport' },
  { code: 'SIN', city: 'Singapore', country: 'Singapore', name: 'Singapore Changi Airport' },
  { code: 'JED', city: 'Jeddah', country: 'Saudi Arabia', name: 'King Abdulaziz International Airport' },
  { code: 'RUH', city: 'Riyadh', country: 'Saudi Arabia', name: 'King Khalid International Airport' },
  { code: 'DOH', city: 'Doha', country: 'Qatar', name: 'Hamad International Airport' },
  { code: 'BAH', city: 'Bahrain', country: 'Bahrain', name: 'Bahrain International Airport' },
  { code: 'MCT', city: 'Muscat', country: 'Oman', name: 'Muscat International Airport' },
  { code: 'KWI', city: 'Kuwait City', country: 'Kuwait', name: 'Kuwait International Airport' },
  { code: 'CAI', city: 'Cairo', country: 'Egypt', name: 'Cairo International Airport' },
  { code: 'NBO', city: 'Nairobi', country: 'Kenya', name: 'Jomo Kenyatta International Airport' },
  { code: 'JNB', city: 'Johannesburg', country: 'South Africa', name: 'O.R. Tambo International Airport' },
  { code: 'SYD', city: 'Sydney', country: 'Australia', name: 'Sydney Kingsford Smith Airport' },
  { code: 'PEK', city: 'Beijing', country: 'China', name: 'Beijing Capital International Airport' },
  { code: 'ICN', city: 'Seoul', country: 'South Korea', name: 'Incheon International Airport' },
  { code: 'NRT', city: 'Tokyo', country: 'Japan', name: 'Narita International Airport' },
  { code: 'FCO', city: 'Rome', country: 'Italy', name: 'Leonardo da Vinci International Airport' },
  { code: 'MAD', city: 'Madrid', country: 'Spain', name: 'Adolfo Suarez Madrid-Barajas Airport' },
  { code: 'AMS', city: 'Amsterdam', country: 'Netherlands', name: 'Amsterdam Airport Schiphol' },
  { code: 'DME', city: 'Moscow', country: 'Russia', name: 'Domodedovo International Airport' },
  { code: 'GYD', city: 'Baku', country: 'Azerbaijan', name: 'Heydar Aliyev International Airport' },
  { code: 'TBS', city: 'Tbilisi', country: 'Georgia', name: 'Tbilisi International Airport' },
  { code: 'EVN', city: 'Yerevan', country: 'Armenia', name: 'Zvartnots International Airport' },
  { code: 'DYU', city: 'Dushanbe', country: 'Tajikistan', name: 'Dushanbe International Airport' },
  { code: 'FRU', city: 'Bishkek', country: 'Kyrgyzstan', name: 'Manas International Airport' },
  { code: 'ALA', city: 'Almaty', country: 'Kazakhstan', name: 'Almaty International Airport' },
  { code: 'TSE', city: 'Astana', country: 'Kazakhstan', name: 'Nursultan Nazarbayev International Airport' },
  { code: 'ASB', city: 'Ashgabat', country: 'Turkmenistan', name: 'Ashgabat International Airport' },
];

const randomNum = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const FALLBACK_AIRLINES = [
  { airline: 'Emirates', code: 'EK', aircraft: 'Boeing 777-300ER', depTerminal: 'Terminal 3', arrTerminal: 'Terminal 1', color: '#D4202C' },
  { airline: 'Flydubai', code: 'FZ', aircraft: 'Boeing 737 MAX 8', depTerminal: 'Terminal 2', arrTerminal: 'Terminal 1', color: '#F26F21' },
  { airline: 'Qatar Airways', code: 'QR', aircraft: 'Boeing 787-9 Dreamliner', depTerminal: 'Terminal 1', arrTerminal: 'Terminal 1', color: '#5C0632' },
];

const FLIGHT_DURATIONS = {
  'DXB-TAS': 3.5, 'DXB-SKD': 3.5, 'DXB-BHK': 3.5, 'DXB-IST': 4.5, 'DXB-LHR': 7.5,
  'DXB-JFK': 14, 'DXB-CDG': 7, 'DXB-FRA': 6.5, 'DXB-DEL': 3.5, 'DXB-BOM': 3,
  'DXB-KHI': 2.5, 'DXB-ISB': 3, 'DXB-LHE': 3, 'DXB-DAC': 5, 'DXB-CMB': 4.5,
  'DXB-KTM': 4.5, 'DXB-MNL': 9, 'DXB-BKK': 6, 'DXB-KUL': 7, 'DXB-SIN': 7,
  'DXB-JED': 3.25, 'DXB-RUH': 2, 'DXB-DOH': 1.25, 'DXB-BAH': 1, 'DXB-MCT': 1.5,
  'DXB-KWI': 2, 'DXB-CAI': 4.25, 'DXB-NBO': 5, 'DXB-JNB': 8, 'DXB-SYD': 13.5,
  'DXB-PEK': 8, 'DXB-ICN': 9, 'DXB-NRT': 10, 'DXB-FCO': 6, 'DXB-MAD': 7.5,
  'DXB-AMS': 7, 'DXB-DME': 5, 'DXB-GYD': 3, 'DXB-TBS': 3.5, 'DXB-EVN': 3.5,
  'DXB-DYU': 4, 'DXB-FRU': 5, 'DXB-ALA': 5, 'DXB-TSE': 5.5, 'DXB-ASB': 3,
  'DXB-AUH': 0.85, 'DXB-SHJ': 0.5, 'DXB-NVI': 4.5,
  'TAS-IST': 5.5, 'TAS-LHR': 8, 'TAS-DME': 4, 'TAS-FRU': 1, 'TAS-DYU': 1.5,
  'TAS-ALA': 2, 'TAS-TSE': 3, 'IST-LHR': 4, 'IST-JFK': 11, 'IST-CDG': 3.5,
};

const REALISTIC_DEPARTURE_HOURS = [6, 7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 19, 20, 21, 22, 23];
const REALISTIC_MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const getFlightDuration = (from, to) => {
  return FLIGHT_DURATIONS[`${from}-${to}`] || FLIGHT_DURATIONS[`${to}-${from}`] || (3 + randomNum(0, 8));
};

const generateRealisticTime = () => {
  const hour = REALISTIC_DEPARTURE_HOURS[randomNum(0, REALISTIC_DEPARTURE_HOURS.length - 1)];
  const minute = REALISTIC_MINUTES[randomNum(0, REALISTIC_MINUTES.length - 1)];
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const calculateArrival = (depTime, durationHours) => {
  const [h, m] = depTime.split(':').map(Number);
  const totalMinutes = h * 60 + m + Math.round(durationHours * 60);
  const arrH = Math.floor(totalMinutes / 60) % 24;
  const arrM = totalMinutes % 60;
  const roundedM = Math.round(arrM / 5) * 5;
  return { time: `${String(arrH).padStart(2, '0')}:${String(roundedM >= 60 ? 0 : roundedM).padStart(2, '0')}`, nextDay: totalMinutes >= 24 * 60 };
};

const getAllFlightsForRoute = (from, to) => {
  const key1 = `${from}-${to}`;
  const key2 = `${to}-${from}`;
  const dbFlights = REAL_FLIGHTS[key1] || REAL_FLIGHTS[key2] || [];
  const flights = dbFlights.map(f => ({ ...f, nextDay: f.nextDay || false, fromDB: true }));
  // If no real flights, generate 3 fallback options
  if (flights.length === 0) {
    for (const fb of FALLBACK_AIRLINES) {
      const depTime = generateRealisticTime();
      const dur = getFlightDuration(from, to);
      const arrival = calculateArrival(depTime, dur);
      const dH = Math.floor(dur);
      const dM = Math.round((dur - dH) * 60);
      flights.push({
        airline: fb.airline, code: fb.code,
        flight: `${fb.code}${randomNum(100, 999)}`,
        dep: depTime, arr: arrival.time,
        duration: `${dH}h ${String(dM).padStart(2, '0')}m`,
        aircraft: fb.aircraft, depTerminal: fb.depTerminal, arrTerminal: fb.arrTerminal,
        nextDay: arrival.nextDay, color: fb.color, fromDB: false,
      });
    }
  }
  return flights;
};

const KeyFlightPage = () => {
  const defaultDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  })();

  const [form, setForm] = useState({
    passengerName: '',
    from: 'DXB',
    to: 'TAS',
    departDate: defaultDate,
    returnDate: '',
    tripType: 'oneway',
  });
  const [step, setStep] = useState('form'); // 'form' | 'select' | 'ticket'
  const [availableFlights, setAvailableFlights] = useState({ outbound: [], return: [] });
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const ticketRef = useRef(null);
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const location = useLocation();

  // Auto-generate demo ticket from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('demo') === '1' && !ticket) {
      const fromAirport = AIRPORTS.find((a) => a.code === 'DXB');
      const toAirport = AIRPORTS.find((a) => a.code === 'TAS');
      const flights = getAllFlightsForRoute('DXB', 'TAS');
      const outFlight = flights[0];
      setTicket({
        outbound: { ...outFlight, from: fromAirport, to: toAirport, date: defaultDate, arrDate: outFlight.nextDay ? addOneDay(defaultDate) : defaultDate, seat: '24A' },
        returnFlight: null,
        passengerName: 'JOHN DOE',
        issueDate: formatIssueDateDoc(),
      });
      setStep('ticket');
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const day = String(d.getDate()).padStart(2, '0');
    const mon = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const yr = d.getFullYear();
    const wd = d.toLocaleDateString('en-US', { weekday: 'short' });
    return `${wd}, ${day} ${mon} ${yr}`;
  };

  const formatIssueDateDoc = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const mon = String(now.getMonth() + 1).padStart(2, '0');
    const yr = now.getFullYear();
    return `${day}.${mon}.${yr}`;
  };

  const addOneDay = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  // Step 1: Show available flights
  const handleSearchFlights = (e) => {
    e.preventDefault();
    if (!form.passengerName || !form.departDate) return;

    const outFlights = getAllFlightsForRoute(form.from, form.to);
    let retFlights = [];
    if (form.tripType === 'return' && form.returnDate) {
      retFlights = getAllFlightsForRoute(form.to, form.from);
    }
    setAvailableFlights({ outbound: outFlights, return: retFlights });
    setSelectedOutbound(null);
    setSelectedReturn(null);
    setStep('select');
  };

  // Step 2: Build ticket from selected flights
  const handleConfirmFlight = () => {
    if (!selectedOutbound) return;
    const fromAirport = AIRPORTS.find((a) => a.code === form.from);
    const toAirport = AIRPORTS.find((a) => a.code === form.to);

    const outbound = {
      ...selectedOutbound,
      from: fromAirport,
      to: toAirport,
      date: form.departDate,
      arrDate: selectedOutbound.nextDay ? addOneDay(form.departDate) : form.departDate,
      seat: `${randomNum(10, 40)}${String.fromCharCode(65 + randomNum(0, 5))}`,
    };

    let returnFlight = null;
    if (form.tripType === 'return' && form.returnDate && selectedReturn) {
      returnFlight = {
        ...selectedReturn,
        from: toAirport,
        to: fromAirport,
        date: form.returnDate,
        arrDate: selectedReturn.nextDay ? addOneDay(form.returnDate) : form.returnDate,
        seat: `${randomNum(10, 40)}${String.fromCharCode(65 + randomNum(0, 5))}`,
      };
    }

    setTicket({
      outbound,
      returnFlight,
      passengerName: form.passengerName.toUpperCase(),
      issueDate: formatIssueDateDoc(),
    });
    setStep('ticket');
  };

  const handleStartOver = () => {
    setTicket(null);
    setStep('form');
    setSelectedOutbound(null);
    setSelectedReturn(null);
    setEmailSent(false);
  };

  const handleDownload = async () => {
    if (!ticketRef.current) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `E-Ticket-${ticket.passengerName.replace(/\s+/g, '-')}.png`;
      a.click();
    } catch {
      window.print();
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddr || !ticketRef.current) return;
    setEmailSending(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });
      const imageData = canvas.toDataURL('image/png');
      const res = await fetch(`${API}/api/send-ticket-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: emailAddr,
          passenger_name: ticket.passengerName,
          route: `${ticket.outbound.from.code} - ${ticket.outbound.to.code}`,
          image_base64: imageData,
        }),
      });
      if (res.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailModal(false), 1500);
      } else {
        alert('Failed to send email. Please try again.');
      }
    } catch {
      alert('Failed to send email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  // Flight card for selection
  const FlightCard = ({ flight, selected, onSelect, label }) => (
    <div
      className={`flt-card ${selected ? 'flt-card-selected' : ''}`}
      onClick={onSelect}
      data-testid={`flt-card-${flight.flight}`}
    >
      <div className="flt-card-airline" style={{ borderLeftColor: flight.color }}>
        <span className="flt-card-airline-name">{flight.airline}</span>
        <span className="flt-card-flight-num">{flight.flight}</span>
      </div>
      <div className="flt-card-route">
        <div className="flt-card-time">
          <strong>{flight.dep}</strong>
        </div>
        <div className="flt-card-arrow">
          <span className="flt-card-duration">{flight.duration}</span>
          <div className="flt-card-line" style={{ background: flight.color }} />
          {flight.nextDay && <span className="flt-card-nextday">+1 day</span>}
        </div>
        <div className="flt-card-time">
          <strong>{flight.arr}</strong>
        </div>
      </div>
      <div className="flt-card-info">
        <span>{flight.aircraft}</span>
        <span>{flight.depTerminal}</span>
      </div>
      {selected && <div className="flt-card-check"><Check size={18} /></div>}
    </div>
  );

  // Professional E-Ticket Itinerary Document
  const ItineraryDocument = ({ data }) => (
    <div className="etk-document" ref={ticketRef}>
      <div className="etk-header">
        <div className="etk-header-right" style={{ width: '100%', textAlign: 'right' }}>
          <div className="etk-doc-title">E-Ticket Itinerary Receipt</div>
          <div className="etk-doc-date">{data.issueDate}</div>
        </div>
      </div>

      <div className="etk-trip-banner" style={{ borderLeftColor: data.outbound.color }}>
        <div className="etk-trip-label">TRIP</div>
        <div className="etk-trip-route">{data.outbound.from.city.toUpperCase()} — {data.outbound.to.city.toUpperCase()}</div>
        <div className="etk-trip-meta">
          <span>Prepared for: <strong>{data.passengerName}</strong></span>
        </div>
      </div>

      <div className="etk-booking-bar">
        <div className="etk-booking-item">
          <span className="etk-booking-label">Status</span>
          <span className="etk-booking-status">CONFIRMED</span>
        </div>
      </div>

      <FlightSegment flight={data.outbound} label="DEPARTURE" />
      {data.returnFlight && <FlightSegment flight={data.returnFlight} label="RETURN" />}

      <div className="etk-section">
        <div className="etk-section-title">Passenger Information</div>
        <table className="etk-table">
          <thead>
            <tr><th>Passenger Name</th><th>Seats</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>{data.passengerName}</strong></td>
              <td>{data.outbound.seat}{data.returnFlight ? ` / ${data.returnFlight.seat}` : ''}</td>
              <td><span className="etk-confirmed-badge">Confirmed</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="etk-notice">
        <div className="etk-notice-title">Important Information</div>
        <ul className="etk-notice-list">
          <li>Please verify flight times prior to departure as schedules may change.</li>
          <li>Check-in opens 48 hours before departure. Online check-in is recommended.</li>
          <li>Carry a valid passport and necessary travel documents at all times.</li>
          <li>Baggage allowance varies by airline and fare class. Contact the airline for details.</li>
        </ul>
      </div>

      <div className="etk-barcode-area">
        <div className="etk-barcode-lines">
          {Array.from({ length: 60 }, (_, i) => (
            <div key={i} className="etk-bar" style={{ width: `${1 + (i % 3)}px`, height: `${30 + (i % 7) * 3}px` }} />
          ))}
        </div>
        <div className="etk-barcode-text">{data.outbound.flight} / {data.outbound.from.code}-{data.outbound.to.code}</div>
      </div>

      <div className="etk-footer" />
    </div>
  );

  const FlightSegment = ({ flight, label }) => (
    <div className="etk-segment">
      <div className="etk-segment-header" style={{ backgroundColor: flight.color }}>
        <span className="etk-segment-airline">{flight.airline}</span>
        <span className="etk-segment-label">{label}</span>
      </div>
      <div className="etk-segment-body">
        <div className="etk-route-visual">
          <div className="etk-rv-point">
            <div className="etk-rv-time">{flight.dep}</div>
            <div className="etk-rv-code">{flight.from.code}</div>
            <div className="etk-rv-city">{flight.from.city}</div>
            <div className="etk-rv-date">{formatDateShort(flight.date)}</div>
          </div>
          <div className="etk-rv-middle">
            <div className="etk-rv-duration">{flight.duration}</div>
            <div className="etk-rv-line">
              <div className="etk-rv-dot" />
              <div className="etk-rv-bar" style={{ background: flight.color }} />
              <Plane size={14} style={{ color: flight.color }} />
              <div className="etk-rv-bar" style={{ background: flight.color }} />
              <div className="etk-rv-dot" />
            </div>
            {flight.nextDay && <div className="etk-rv-nextday">+1 day</div>}
          </div>
          <div className="etk-rv-point">
            <div className="etk-rv-time">{flight.arr}</div>
            <div className="etk-rv-code">{flight.to.code}</div>
            <div className="etk-rv-city">{flight.to.city}</div>
            <div className="etk-rv-date">{formatDateShort(flight.arrDate)}</div>
          </div>
        </div>
        <table className="etk-table etk-table-details">
          <thead>
            <tr><th>Flight</th><th>Aircraft</th><th>Class</th><th>Departure Terminal</th><th>Arrival Terminal</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>{flight.flight}</strong></td>
              <td>{flight.aircraft}</td>
              <td>Economy</td>
              <td>{flight.depTerminal}</td>
              <td>{flight.arrTerminal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="kf-page" data-testid="keyflight-page">
      <Helmet>
        <title>Free Dummy Flight Ticket for Visa Application 2026 | Instant E-Ticket Generator</title>
        <meta name="description" content="Generate a free dummy flight ticket for your visa application in seconds. Realistic e-ticket itinerary with real airlines like Emirates, Flydubai, Turkish Airlines. Choose from 50+ airports worldwide. No registration, no payment — download or email your ticket instantly." />
        <meta name="keywords" content="dummy flight ticket for visa, free dummy ticket, fake flight ticket generator, flight reservation for visa application, dummy airline ticket, sample e-ticket itinerary, flight itinerary for embassy, visa dummy ticket free, dummy ticket for Schengen visa, dummy ticket for UK visa, dummy ticket for US visa, flight booking confirmation for visa, onward ticket for visa, return ticket for visa, Dubai flight ticket, Emirates dummy ticket, Flydubai dummy ticket, dummy ticket generator 2026, keyflight, free flight reservation" />
        <link rel="canonical" href="https://registantours.com/keyflight" />
        <meta property="og:title" content="Free Dummy Flight Ticket for Visa Application | Instant Generator" />
        <meta property="og:description" content="Create a realistic dummy flight ticket for your visa application. 50+ airports, real airlines, instant download or email. 100% free, no signup required." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/keyflight" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Dummy Flight Ticket for Visa Application 2026" />
        <meta name="twitter:description" content="Generate realistic e-ticket itineraries with real airlines. Free, instant, no registration. Perfect for Schengen, UK, US visa applications." />
      </Helmet>
      <HreflangTags path="/keyflight" />

      <header className="dark-header" data-testid="kf-header">
        <div className="header-left">
          <div className="header-logo">
            <Link to="/"><img src={currentLogo} alt="Registan" className="logo-img" /></Link>
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/tourismsoftware" className="dark-nav-link">{t('tourismSoftware') || 'Tourism Software'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>

        {isAuthenticated ? (
          <div className="header-user-menu">
            <NotificationBell userEmail={user?.email} />
            <button className="btn-user-profile" onClick={() => window.location.href = '/dashboard'}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout"><LogOut size={20} /></button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => window.location.href = '/login'}>{t('login')}</button>
            <button className="btn-primary btn-signup" onClick={() => window.location.href = '/register'}>{t('signUp')}</button>
          </div>
        )}

        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)} data-testid="kf-mobile-menu-toggle">
          <Menu size={24} />
        </button>
      </header>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="kf-main tool-page-content">
        <div className="kf-hero">
          <h1 className="kf-title" data-testid="kf-title">{t('flightTicketGenerator') || 'Flight Ticket Generator'}</h1>
          <p className="kf-subtitle">{t('flightTicketSubtitle') || 'Generate professional e-ticket itineraries for visa applications. Select any route worldwide.'}</p>
        </div>

        {/* Step 1: Form */}
        {step === 'form' && (
          <form className="kf-form" onSubmit={handleSearchFlights} data-testid="kf-form">
            <div className="kf-form-row">
              <div className="kf-field">
                <label><User size={14} /> {t('passengerName') || 'Passenger Name'}</label>
                <input type="text" name="passengerName" value={form.passengerName} onChange={handleChange} placeholder="JOHN DOE" required data-testid="kf-passenger-name" />
              </div>
              <div className="kf-field kf-field-trip">
                <label>{t('tripType') || 'Trip Type'}</label>
                <select name="tripType" value={form.tripType} onChange={handleChange} data-testid="kf-trip-type">
                  <option value="oneway">{t('oneWay') || 'One Way'}</option>
                  <option value="return">{t('roundTrip') || 'Round Trip'}</option>
                </select>
              </div>
            </div>
            <div className="kf-form-row">
              <div className="kf-field">
                <label><MapPin size={14} /> {t('from') || 'From'}</label>
                <select name="from" value={form.from} onChange={handleChange} data-testid="kf-from">
                  {AIRPORTS.map((a) => (<option key={a.code} value={a.code}>{a.code} - {a.city}, {a.country}</option>))}
                </select>
              </div>
              <div className="kf-field">
                <label><MapPin size={14} /> {t('to') || 'To'}</label>
                <select name="to" value={form.to} onChange={handleChange} data-testid="kf-to">
                  {AIRPORTS.map((a) => (<option key={a.code} value={a.code}>{a.code} - {a.city}, {a.country}</option>))}
                </select>
              </div>
            </div>
            <div className="kf-form-row">
              <div className="kf-field">
                <label><Calendar size={14} /> {t('departureDate') || 'Departure Date'}</label>
                <input type="date" name="departDate" value={form.departDate} onChange={handleChange} required data-testid="kf-depart-date" />
              </div>
              {form.tripType === 'return' && (
                <div className="kf-field">
                  <label><Calendar size={14} /> {t('returnDate') || 'Return Date'}</label>
                  <input type="date" name="returnDate" value={form.returnDate} onChange={handleChange} data-testid="kf-return-date" />
                </div>
              )}
            </div>
            <button type="submit" className="kf-generate-btn" data-testid="kf-generate-btn">
              <Plane size={18} /> {t('searchFlights') || 'Search Flights'}
            </button>
          </form>
        )}

        {/* Step 2: Flight Selection */}
        {step === 'select' && (
          <div className="kf-select-section" data-testid="kf-select-section">
            <div className="kf-select-header">
              <h2>{t('selectYourFlight') || 'Select Your Flight'}</h2>
              <p>{AIRPORTS.find(a => a.code === form.from)?.city} to {AIRPORTS.find(a => a.code === form.to)?.city} &middot; {formatDateShort(form.departDate)}</p>
            </div>

            <div className="kf-select-label">{t('outboundFlights') || 'Outbound Flights'}</div>
            <div className="kf-flight-list" data-testid="kf-outbound-list">
              {availableFlights.outbound.map((f, i) => (
                <FlightCard key={i} flight={f} selected={selectedOutbound?.flight === f.flight} onSelect={() => setSelectedOutbound(f)} label="outbound" />
              ))}
            </div>

            {availableFlights.return.length > 0 && (
              <>
                <div className="kf-select-label" style={{ marginTop: 24 }}>{t('returnFlights') || 'Return Flights'}</div>
                <div className="kf-flight-list" data-testid="kf-return-list">
                  {availableFlights.return.map((f, i) => (
                    <FlightCard key={i} flight={f} selected={selectedReturn?.flight === f.flight} onSelect={() => setSelectedReturn(f)} label="return" />
                  ))}
                </div>
              </>
            )}

            <div className="kf-select-actions">
              <button className="kf-btn kf-btn-new" onClick={() => setStep('form')} data-testid="kf-back-btn">{t('back') || 'Back'}</button>
              <button
                className="kf-btn kf-btn-download"
                onClick={handleConfirmFlight}
                disabled={!selectedOutbound}
                data-testid="kf-confirm-flight-btn"
              >
                <Check size={18} /> {t('generateTicket') || 'Generate Ticket'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Ticket Result - hidden for capture, only show actions */}
        {step === 'ticket' && ticket && (
          <div className="kf-result" data-testid="kf-result">
            {/* Hidden off-screen for html2canvas capture */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
              <ItineraryDocument data={ticket} />
            </div>

            <div className="kf-done-message" data-testid="kf-done-message">
              <Check size={40} className="kf-done-icon" />
              <h2>{t('yourTicketReady') || 'Your E-Ticket is Ready!'}</h2>
              <p>{ticket.passengerName} &middot; {ticket.outbound.from.code} to {ticket.outbound.to.code} &middot; {ticket.outbound.airline} {ticket.outbound.flight}</p>
            </div>

            <div className="kf-actions">
              <button className="kf-btn kf-btn-download" onClick={handleDownload} data-testid="kf-download-btn">
                <Download size={18} /> {t('downloadETicket') || 'Download E-Ticket'}
              </button>
              <button className="kf-btn kf-btn-email" onClick={() => { setEmailModal(true); setEmailSent(false); }} data-testid="kf-email-btn">
                <Mail size={18} /> {t('sendToEmail') || 'Send to Email'}
              </button>
              <button className="kf-btn kf-btn-new" onClick={handleStartOver} data-testid="kf-new-btn">
                <RefreshCw size={18} /> {t('generateNew') || 'Generate New'}
              </button>
            </div>
          </div>
        )}

        {/* Email Modal */}
        {emailModal && (
          <div className="kf-email-overlay" onClick={() => setEmailModal(false)} data-testid="kf-email-overlay">
            <div className="kf-email-modal" onClick={(e) => e.stopPropagation()} data-testid="kf-email-modal">
              <button className="kf-email-close" onClick={() => setEmailModal(false)}><X size={20} /></button>
              {emailSent ? (
                <div className="kf-email-success">
                  <Check size={40} />
                  <p>{t('ticketSentSuccess') || 'E-Ticket sent successfully!'}</p>
                </div>
              ) : (
                <>
                  <h3>{t('sendTicketTitle') || 'Send E-Ticket to Email'}</h3>
                  <p>{t('sendTicketDesc') || 'Enter the email address to receive the e-ticket itinerary.'}</p>
                  <input
                    type="email"
                    value={emailAddr}
                    onChange={(e) => setEmailAddr(e.target.value)}
                    placeholder="email@example.com"
                    className="kf-email-input"
                    data-testid="kf-email-input"
                  />
                  <button
                    className="kf-btn kf-btn-download"
                    onClick={handleSendEmail}
                    disabled={!emailAddr || emailSending}
                    style={{ width: '100%', justifyContent: 'center' }}
                    data-testid="kf-email-send-btn"
                  >
                    <Mail size={18} />
                    {emailSending ? (t('sending') || 'Sending...') : (t('sendETicket') || 'Send E-Ticket')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default KeyFlightPage;
